import { useCallback, useEffect, useRef, useState } from 'react';

import { getErrorMessage, getPlaylists, Playlist } from '@/lib/api/playlist';

/**
 * Custom hook for playlist data management
 * Handles fetching and managing playlist list state
 *
 * @param programId Program ID (optional)
 * @param initialLimit Initial display limit (optional, no limit if undefined)
 * @param initialOffset Initial offset (optional)
 * @param silentRefresh Silent refresh mode - if true, continues to display previous data during fetch (optional)
 * @param enabled Controls whether the hook fetches data (optional, default true)
 * @param refreshInterval Optional interval for automatic refresh (in ms)
 */
export const usePlaylistData = (
  programId?: number,
  initialLimit?: number,
  initialOffset: number = 0,
  silentRefresh: boolean = false,
  enabled: boolean = true, // Controls whether the hook fetches data
  refreshInterval?: number, // Optional interval for automatic refresh (in ms)
) => {
  // Playlist list
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  // Loading state
  const [loading, setLoading] = useState(enabled); // Start loading only if enabled
  // Error state
  const [error, setError] = useState<string | null>(null);
  // Limit
  const [limit, setLimit] = useState(initialLimit);
  // Offset
  const [offset, setOffset] = useState(initialOffset);
  // Total count
  const [total, setTotal] = useState(0);
  // Ref to store interval ID
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Function to fetch playlist list
  const fetchPlaylists = useCallback(async () => {
    // Do nothing if not enabled or no programId
    if (!enabled || !programId) {
      // If disabled ensure loading is false and playlists are cleared
      if (loading) setLoading(false);
      if (playlists.length > 0) setPlaylists([]);
      return;
    }

    // Only show loading state if not in silent refresh mode or first load
    if (!silentRefresh || playlists.length === 0) {
      setLoading(true);
    }
    setError(null);

    try {
      // Fetch playlist list from API
      const response = await getPlaylists(
        undefined, // at
        undefined, // since
        undefined, // until
        programId,
        limit,
        offset,
        'since:desc', // Newest first
      );

      setPlaylists(response.data || []);

      // Update pagination information
      if (response.meta.pagination) {
        setTotal(response.meta.pagination.total);
      }
    } catch (err) {
      console.error('Failed to fetch playlists:', err);
      // Convert error message to user-friendly format
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [programId, limit, offset, silentRefresh, enabled]); // Removed playlists.length and loading

  // Effect for initial fetch and managing periodic refresh interval
  useEffect(() => {
    // Clear any existing interval when dependencies change
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (enabled) {
      fetchPlaylists(); // Initial fetch when enabled

      // Set up new interval if refreshInterval is provided
      if (refreshInterval && refreshInterval > 0) {
        intervalRef.current = setInterval(fetchPlaylists, refreshInterval);
      }
    } else {
      // If disabled, ensure loading is false and playlists are cleared
      if (loading) setLoading(false);
      if (playlists.length > 0) setPlaylists([]);
    }

    // Cleanup function to clear interval on unmount or before re-running effect
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, refreshInterval, fetchPlaylists]); // Dependencies: enabled, refreshInterval, and fetchPlaylists

  // Reset offset when program ID changes (only if enabled)
  useEffect(() => {
    if (enabled) {
      setOffset(0);
    }
  }, [programId, enabled]);

  return {
    // State
    playlists,
    loading,
    error,
    limit,
    offset,
    total,

    // Actions
    setLimit,
    setOffset,
    refreshData: fetchPlaylists, // Expose fetch function for manual refresh if needed
  };
};

export default usePlaylistData;
